/* =========================================================
   SynEra — Motion layer
   - Scroll-triggered fade-ups (.reveal → .is-visible)
   - Per-element stagger via data-delay="<ms>"
   - Subtle parallax on hero media (data-parallax="<strength>")
   - Header background state when scrolled
   - Respects prefers-reduced-motion
   ========================================================= */
(() => {
  'use strict';

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- 1. Reveal on scroll ---------------------------------- */
  const revealEls = document.querySelectorAll('.reveal');

  if (prefersReduced || !('IntersectionObserver' in window)) {
    revealEls.forEach(el => el.classList.add('is-visible'));
  } else {
    const io = new IntersectionObserver((entries, observer) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const el = entry.target;
        const delay = parseInt(el.dataset.delay, 10) || 0;
        if (delay > 0) {
          el.style.transitionDelay = `${delay}ms`;
        }
        el.classList.add('is-visible');
        observer.unobserve(el);
      }
    }, {
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.12
    });

    revealEls.forEach(el => io.observe(el));
  }

  /* ---- 2. Parallax on hero media ---------------------------- */
  if (!prefersReduced) {
    const parallaxEls = document.querySelectorAll('[data-parallax]');
    if (parallaxEls.length) {
      let ticking = false;

      const applyParallax = () => {
        const vh = window.innerHeight;
        parallaxEls.forEach(el => {
          const rect = el.getBoundingClientRect();
          // Only transform when the element is near the viewport
          if (rect.bottom < -200 || rect.top > vh + 200) return;
          const strength = parseFloat(el.dataset.parallax) || 0.1;
          const centerOffset = rect.top + rect.height / 2 - vh / 2;
          const translate = -(centerOffset * strength);
          // Transform the element itself so all stacked layers (jar, aura, light
          // mask, floor) move together as a single object.
          el.style.setProperty('--parallax-y', `${translate.toFixed(1)}px`);
          el.style.transform = `translate3d(0, ${translate.toFixed(1)}px, 0)`;
        });
        ticking = false;
      };

      const onScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(applyParallax);
          ticking = true;
        }
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', applyParallax, { passive: true });
      applyParallax();
    }
  }

  /* ---- 3. Sticky header scroll state ------------------------ */
  const header = document.getElementById('site-header');
  if (header) {
    const setHeaderState = () => {
      if (window.scrollY > 8) header.classList.add('is-scrolled');
      else header.classList.remove('is-scrolled');
    };
    window.addEventListener('scroll', setHeaderState, { passive: true });
    setHeaderState();
  }

  /* ---- 4. Smooth scroll offset for anchors ------------------ */
  // CSS handles scroll-behavior: smooth; this compensates for the sticky header.
  document.querySelectorAll('a[href^="#"]').forEach(link => {
    link.addEventListener('click', (e) => {
      const id = link.getAttribute('href').slice(1);
      if (!id) return;
      const target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      const headerH = header ? header.offsetHeight : 0;
      const y = target.getBoundingClientRect().top + window.scrollY - headerH + 1;
      window.scrollTo({ top: y, behavior: prefersReduced ? 'auto' : 'smooth' });
      // Update URL hash without jump
      if (history.pushState) history.pushState(null, '', `#${id}`);
    });
  });

  /* ---- 5. Newsletter form hint (no backend yet) ----------------
     Form has type="email" + required so the browser's native
     validation runs first. preventDefault is conditional on
     validity so an invalid submit (empty / typo) shows the
     browser's tooltip instead of silently reporting success. */
  const nl = document.querySelector('.newsletter');
  if (nl) {
    nl.addEventListener('submit', (e) => {
      const input = nl.querySelector('input');
      if (!input) { e.preventDefault(); return; }
      if (!input.checkValidity()) {
        input.reportValidity();
        e.preventDefault();
        return;
      }
      // Valid email — block actual submit (no backend wired yet)
      // and swap UI to a thank-you state.
      e.preventDefault();
      const btnLabel = nl.querySelector('button span:first-child');
      if (btnLabel) btnLabel.textContent = 'Thank you';
      input.value = '';
      input.placeholder = 'You\'re on the list.';
      input.disabled = true;
    });
  }

  /* ---- 6. Purchase option — subtle UX polish ---------------- */
  // (The visible state is handled in CSS via :has(input:checked). This
  //  keeps keyboard accessibility on the radios clean.)

  /* ---- 7. Hero jar cursor-proximity glow -------------------- */
  /* Warms the jar as the pointer approaches. A radial aura follows the
     cursor behind the jar, and a masked duplicate of the jar on top
     (mix-blend-mode: screen) lights up only the pixels near the pointer. */
  (() => {
    if (prefersReduced) return;
    const jar = document.querySelector('.hero-jar');
    if (!jar) return;

    let ticking = false;
    let lastEvent = null;

    const update = (e) => {
      const r = jar.getBoundingClientRect();
      if (r.width === 0) return;

      // Cursor position relative to the jar box, expressed as a percentage.
      const mx = ((e.clientX - r.left) / r.width) * 100;
      const my = ((e.clientY - r.top) / r.height) * 100;

      // Proximity from the center of the jar — 1.0 at center, 0 at max range.
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.hypot(dx, dy);
      // Range extends a little past the jar so the glow starts before the
      // cursor crosses the edge.
      const maxDist = Math.max(r.width, r.height) * 0.85;
      const intensity = Math.max(0, Math.min(1, 1 - dist / maxDist));

      jar.style.setProperty('--mx', `${mx.toFixed(1)}%`);
      jar.style.setProperty('--my', `${my.toFixed(1)}%`);
      jar.style.setProperty('--glow', intensity.toFixed(3));
    };

    const onMove = (e) => {
      lastEvent = e;
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(() => {
          if (lastEvent) update(lastEvent);
          ticking = false;
        });
      }
    };

    const onLeave = () => {
      jar.style.setProperty('--glow', '0');
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerleave', onLeave);
    // Also clear glow for pure-touch devices where the pointer can disappear.
    window.addEventListener('blur', onLeave);
  })();

  /* ---- 8. Science section — polished rose-gold double helix ---
     Renders two continuous reflective ribbons twisting around a
     shared meandering axis. Each ribbon is filled with a transverse
     linear gradient simulating a cylindrical metal surface:
        deep burgundy shadow → rose-gold → hot near-white highlight
        → rose-gold → deep burgundy shadow.
     The position of the hot highlight stop shifts as the strand twists,
     so the bright streak appears to slide along the surface the way
     a real environment reflection moves on polished chrome.
     Thin reflective rungs join the two strands.
     All segments are z-sorted back-to-front so back-facing strand
     portions correctly disappear behind front-facing ones.
     ---- Reversion note ----
     If this ribbon look isn't right, the previous particle-based
     implementation is preserved at motion.particles.bak.js —
     copy that file over this one to roll back. */
  (() => {
    const canvas = document.querySelector('.science-dna');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const section = canvas.parentElement;

    // --- palette (metal rose-gold) --------------------------
    // Dark → light, in the order they appear across a cylinder cross-section
    // when lit from roughly "above-front". Deep burgundy on the shadow side,
    // rose-gold in the belly, champagne near the lit side, hot near-white at
    // the specular highlight. On a cream background these values ride the
    // full tonal range so the ribbons read as solid reflective material.
    // More tonal stops = smoother cross-section gradients, no hard edges.
    const SHADOW_DEEP  = '#3A1B11';   // dark burgundy — eased up from near-black so the edge doesn't read as a cliff
    const SHADOW_WARM  = '#4C261B';   // mid-dark blend stop
    const SHADOW_ROSE  = '#6B382A';   // warm shadow edge
    const ROSE_SOFT    = '#945A40';   // shadow → rose-gold transition
    const ROSE_GOLD    = '#C48E6E';   // brand rose-gold (midtone)
    const ROSE_LIGHT   = '#DBAD8B';   // rose-gold → highlight transition
    const HIGHLIGHT    = '#F5CEA8';   // lit side
    const HIGHLIGHT_HI = '#FBDFBF';   // highlight → specular transition
    const HOT_SPEC     = '#FFF4E2';   // hot specular streak (near-white)
    const RUNG_SHADOW  = '#3F1F14';
    const RUNG_MID     = '#A6714F';
    const RUNG_HI      = '#F2D0AC';

    // --- continuous color ramp ------------------------------
    // Instead of selecting a single "hotColor" from a few thresholds
    // (which produces visible banding where adjacent segments fall on
    // either side of a threshold), we treat the palette as a continuous
    // 0..1 ramp and interpolate within it. Every gradient stop along
    // every cross-section is an exact lerp, so adjacent segments
    // differ only infinitesimally — no steps, no seams.
    function _hx(h) {
      const n = parseInt(h.slice(1), 16);
      return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
    function _mix(a, b, t) {
      return [
        a[0] + (b[0] - a[0]) * t,
        a[1] + (b[1] - a[1]) * t,
        a[2] + (b[2] - a[2]) * t
      ];
    }
    function _rgb(c) {
      return 'rgb(' + (c[0]|0) + ',' + (c[1]|0) + ',' + (c[2]|0) + ')';
    }
    // Ramp anchors: ramp position (0 = deepest shadow, 1 = hottest specular)
    // → RGB. Spacing tuned so the mid-range rose-gold has the most room
    // (that's where the eye spends the most time on a metal surface).
    const RAMP = [
      [0.00, _hx(SHADOW_DEEP)],
      [0.10, _hx(SHADOW_WARM)],
      [0.22, _hx(SHADOW_ROSE)],
      [0.38, _hx(ROSE_SOFT)],
      [0.56, _hx(ROSE_GOLD)],
      [0.72, _hx(ROSE_LIGHT)],
      [0.86, _hx(HIGHLIGHT)],
      [0.94, _hx(HIGHLIGHT_HI)],
      [1.00, _hx(HOT_SPEC)]
    ];
    function rampAt(p) {
      if (p <= 0) return RAMP[0][1];
      if (p >= 1) return RAMP[RAMP.length - 1][1];
      for (let i = 1; i < RAMP.length; i++) {
        if (p <= RAMP[i][0]) {
          const t = (p - RAMP[i-1][0]) / (RAMP[i][0] - RAMP[i-1][0]);
          return _mix(RAMP[i-1][1], RAMP[i][1], t);
        }
      }
      return RAMP[RAMP.length - 1][1];
    }
    // Soft cosine halo used for traveling sheen — returns 0..1.
    function cosHalo(s, sCrest, width) {
      const d = Math.abs(s - sCrest);
      if (d >= width) return 0;
      const t = d / width;
      return 0.5 * (1 + Math.cos(PI * t));
    }

    // --- state ----------------------------------------------
    let W = 0, H = 0, dpr = 1;
    const pointer = { x: -9999, y: -9999, active: false };
    let visible = false;
    let rafId = 0;
    let t0 = performance.now();
    let lastTick = t0;

    // --- helpers --------------------------------------------
    const clamp = (v, lo, hi) => v < lo ? lo : (v > hi ? hi : v);
    const PI = Math.PI;

    // --- tuning ---------------------------------------------
    const TWIST_CYCLES  = 1.35;       // full 360° rotations across the length — fewer turns = airier, less compressed
    const TWIST_SPEED   = 0.000065;   // rad / ms
    const BREATHE_SPEED = 0.00012;
    const BREATHE_AMT   = 0.08;
    // Meandering axis — two incommensurate waves, slowly drifting
    const AXIS_AMP1     = 0.10;
    const AXIS_AMP2     = 0.045;
    const AXIS_W1       = 1.6;        // slower primary wave — with taller axis, fewer bends read as "organic" not "wiggly"
    const AXIS_W2       = 3.2;        // slower secondary too
    const AXIS_DRIFT1   = 0.00002;
    const AXIS_DRIFT2   = 0.000015;
    // Ribbon sampling + thickness (responsive, set in resize())
    let N_SAMPLES        = 200;
    let STRAND_THICKNESS = 28;
    let RUNG_COUNT       = 26;
    let RUNG_THICKNESS   = 6;
    // Traveling highlight streaks (environment reflection sliding along the
    // length of each strand). Two crests per strand, drifting at different
    // speeds so the composition never reads as periodic.
    const SHEEN_SPEED_A  = 0.000060;  // pos / ms
    const SHEEN_SPEED_B  = -0.000042;
    const SHEEN_WIDTH    = 0.22;      // wider = softer falloff; no hard halo edge
    const SHEEN_WIDTH2   = 0.34;      // broader ambient wash that always overlaps

    // --- per-frame sample arrays ----------------------------
    // Re-allocated on resize; re-populated every frame.
    let strandA = null, strandB = null;   // arrays of {s, cx, cy, z, twist, px, py}
    let sheenA  = 0;                       // longitudinal sheen positions in [0,1]
    let sheenB  = 0.5;
    let sheenC  = 0.25;

    function allocStrand(n) {
      const arr = new Array(n);
      for (let i = 0; i < n; i++) {
        arr[i] = { s: 0, cx: 0, cy: 0, z: 0, twist: 0, px: 0, py: 0 };
      }
      return arr;
    }

    // --- axis + strand sample computation -------------------
    // VERTICAL helix: the centerline runs top-to-bottom on the right side
    // of the section with a slight horizontal meander so it reads as
    // organic metal sculpture rather than a ruled line.  The strands
    // wrap around this vertical axis, so the twist ribbons extend
    // left/right from it — staying within a narrow right-hand band
    // that never touches the copy.
    function sampleAxis(s, time, out) {
      // Overshoot the section on both ends so the strands exit the frame
      // top and bottom — the viewer never sees a "start" or "finish"
      // terminator, which sells the idea of a continuous sculpture.
      const yTop = H * -0.18;
      const yBot = H *  1.18;
      const ySpan = yBot - yTop;

      // Anchor column. Desktop: left side (copy lives on the right). Mobile:
      // the ribbon stands as a single vertical column. Pulled in slightly
      // from the right edge so a bit MORE than half the helix is visible
      // — the right swings still clip past the canvas, but the visible
      // portion is wider. Depth comes from perspective scaling below
      // (radius + thickness) plus the atmospheric mask in CSS, NOT from
      // curving the path. A straight column receding toward a vanishing
      // point reads as real 3D; a curved path reads as decorative.
      const xAnchor = W < 700 ? W * 0.88 : W * 0.20;

      // Horizontal meander — two slow, incommensurate waves whose phases
      // drift over time so the column never quite repeats. Mobile values
      // tightened from earlier so the half-helix doesn't read as bulky.
      const mA = W < 700 ? 22 : 38;    // primary wobble amplitude in px
      const mB = W < 700 ?  9 : 16;    // secondary
      const ph1 = time * AXIS_DRIFT1;
      const ph2 = time * AXIS_DRIFT2 + 1.1;
      const axisX = xAnchor
                  + mA * Math.sin(s * AXIS_W1 * PI + ph1)
                  + mB * Math.sin(s * AXIS_W2 * PI + ph2);
      const axisY = yTop + s * ySpan;

      // Tangent to the centerline curve (d/ds).
      const dxds = mA * AXIS_W1 * PI * Math.cos(s * AXIS_W1 * PI + ph1)
                 + mB * AXIS_W2 * PI * Math.cos(s * AXIS_W2 * PI + ph2);
      const dyds = ySpan;
      const tLen = Math.hypot(dxds, dyds) || 1;

      // Unit normal (tangent rotated 90° CCW). For a mostly-vertical axis
      // this is mostly horizontal — exactly what we need for the helix
      // strands to wrap left/right around the column.
      const nX = -dyds / tLen;
      const nY =  dxds / tLen;

      // Helix radius — desktop occupies the rightmost 300-380px; mobile
      // gets ~28% of viewport width at full size, so the half-helix
      // anchored at the right edge reads as a tall metal column.
      const ampRaw = W < 700 ? Math.min(W * 0.28, 120) : Math.min(W * 0.11, 140);
      // Mobile perspective: power curve so the ribbon foreshortens at the
      // top (far / vanishing point) and reaches full width at the bottom
      // (near / camera). Floor raised to 0.48 so the top of the helix
      // doesn't thin out into a wire — it stays present as receding metal.
      // Desktop = no scaling.
      const perspFactor = W < 700
        ? 0.48 + 0.52 * Math.pow(s, 1.3)
        : 1.0;
      const ampBase = ampRaw * perspFactor;
      const amp = ampBase
                * (1 + Math.sin(time * BREATHE_SPEED + s * 0.6) * BREATHE_AMT);

      out.axisX = axisX;
      out.axisY = axisY;
      out.nX    = nX;
      out.nY    = nY;
      out.amp   = amp;
    }

    const _axis = {};
    function sampleStrand(s, strandIdx, time, dst) {
      sampleAxis(s, time, _axis);
      const twist = s * TWIST_CYCLES * PI * 2 + time * TWIST_SPEED + strandIdx * PI;
      const ct = Math.cos(twist);
      const st = Math.sin(twist);
      dst.s     = s;
      dst.cx    = _axis.axisX + _axis.amp * ct * _axis.nX;
      dst.cy    = _axis.axisY + _axis.amp * ct * _axis.nY;
      dst.z     = st;     // depth in [−1, 1]
      dst.twist = twist;
      // perpendicular filled in by computePerpendiculars()
    }

    function resampleStrands(time) {
      const n = N_SAMPLES + 1;
      for (let i = 0; i < n; i++) {
        const s = i / N_SAMPLES;
        sampleStrand(s, 0, time, strandA[i]);
        sampleStrand(s, 1, time, strandB[i]);
      }
      computePerpendiculars(strandA);
      computePerpendiculars(strandB);
    }

    // Per-sample 2D perpendicular = unit tangent rotated 90°.
    // Used to offset the ribbon edges by ± thickness/2.
    function computePerpendiculars(arr) {
      const n = arr.length;
      for (let i = 0; i < n; i++) {
        let dx, dy;
        if (i === 0) {
          dx = arr[1].cx - arr[0].cx;
          dy = arr[1].cy - arr[0].cy;
        } else if (i === n - 1) {
          dx = arr[i].cx - arr[i-1].cx;
          dy = arr[i].cy - arr[i-1].cy;
        } else {
          dx = arr[i+1].cx - arr[i-1].cx;
          dy = arr[i+1].cy - arr[i-1].cy;
        }
        const L = Math.hypot(dx, dy) || 1;
        arr[i].px = -dy / L;
        arr[i].py =  dx / L;
      }
    }

    // --- render a single ribbon segment ---------------------
    // Each segment is a quadrilateral between sample i and i+1, filled with
    // a transverse linear gradient (shadow → rose → highlight → rose →
    // shadow). The position of the hot highlight stop within the gradient
    // slides according to (twist + longitudinal sheen), giving the
    // unmistakable "light streaming across polished metal" effect.
    // Reusable cross-section gradient stop positions — 15 evenly spaced
    // sample points across u∈[0,1]. Each segment computes the ramp
    // position at each u and places a stop there, producing a fully
    // continuous cross-section gradient (no thresholds, no steps).
    const XS_STOPS = 15;

    function drawStrandSegment(a, b) {
      // Mobile perspective: each strand sample is thinner at the top of
      // the section (far) and thicker toward the bottom (near). Power
      // curve matches the helix-radius scaling in sampleAxis so the
      // ribbon's width and thickness foreshorten together. Floor 0.48
      // keeps the top from becoming wire-thin.
      const tA = (W < 700) ? 0.48 + 0.52 * Math.pow(a.s, 1.3) : 1.0;
      const tB = (W < 700) ? 0.48 + 0.52 * Math.pow(b.s, 1.3) : 1.0;
      const rA = STRAND_THICKNESS * 0.5 * tA;
      const rB = STRAND_THICKNESS * 0.5 * tB;
      let a1x = a.cx + a.px * rA, a1y = a.cy + a.py * rA;
      let a2x = a.cx - a.px * rA, a2y = a.cy - a.py * rA;
      let b1x = b.cx + b.px * rB, b1y = b.cy + b.py * rB;
      let b2x = b.cx - b.px * rB, b2y = b.cy - b.py * rB;
      // Average radius for any downstream calculations that still want
      // a single thickness reference.
      const r = (rA + rB) * 0.5;

      // Extend each end along the tangent by ~1px so adjacent quads
      // overlap slightly — kills sub-pixel seam artifacts at the joints.
      const tx = b.cx - a.cx, ty = b.cy - a.cy;
      const tL = Math.hypot(tx, ty);
      if (tL > 0.001) {
        const ex = (tx / tL) * 1.1;
        const ey = (ty / tL) * 1.1;
        a1x -= ex; a1y -= ey; a2x -= ex; a2y -= ey;
        b1x += ex; b1y += ey; b2x += ex; b2y += ey;
      }

      // Gradient is transverse to the strand, averaged across the segment.
      const gx1 = (a1x + b1x) * 0.5, gy1 = (a1y + b1y) * 0.5;
      const gx2 = (a2x + b2x) * 0.5, gy2 = (a2y + b2y) * 0.5;
      const grad = ctx.createLinearGradient(gx1, gy1, gx2, gy2);

      // Depth factor: 0 back-facing, 1 front-facing (dots toward viewer lit).
      const avgZ = (a.z + b.z) * 0.5;
      const frontness = (avgZ + 1) * 0.5;

      // Traveling sheen — three overlapping wide cosine halos so the
      // light sliding along the strand reads as a soft, continuously
      // varying reflection rather than a spotlight with a hard edge.
      const sMid = (a.s + b.s) * 0.5;
      const sheen =
        cosHalo(sMid, sheenA, SHEEN_WIDTH)  * 1.00 +
        cosHalo(sMid, sheenB, SHEEN_WIDTH)  * 0.85 +
        cosHalo(sMid, sheenC, SHEEN_WIDTH2) * 0.60;

      // Hot-streak position in the cross-section (sweeps around the tube
      // with twist). Continuous — no clamping to a visible threshold.
      const tw = (a.twist + b.twist) * 0.5;
      const hotPos = clamp(0.5 + 0.20 * Math.sin(tw * 0.5 + 0.6), 0.22, 0.78);

      // Peak ramp position at the hot spot: back-facing no-sheen settles
      // around rose-soft (0.35); fully lit + sheen goes to specular (1.0).
      // Smooth lerp, no step thresholds.
      const litFactor = clamp(frontness * 0.72 + Math.min(sheen, 1.4) * 0.45, 0, 1);
      const peak = 0.30 + 0.70 * litFactor;

      // Cross-section shading: at each transverse position u, compute a
      // cosine bell centered at hotPos whose peak value = 'peak'. Add a
      // tiny ambient floor so edges aren't pitch-black on lit segments
      // (prevents a visible seam between strand and background).
      const halfL = hotPos;          // half-width to the low edge (u=0)
      const halfR = 1 - hotPos;      // half-width to the high edge (u=1)
      for (let i = 0; i < XS_STOPS; i++) {
        const u = i / (XS_STOPS - 1);
        // Normalized distance from hot position ∈ [0,1]
        const half = u < hotPos ? halfL : halfR;
        const d = half > 1e-4 ? Math.abs(u - hotPos) / half : 1;
        // Cosine bell: 1 at hotPos, 0 at each edge
        const bell = 0.5 * (1 + Math.cos(PI * clamp(d, 0, 1)));
        // Edges darken with a gentle power curve so the outer silhouette
        // is slightly richer than a linear falloff.
        const shaped = Math.pow(bell, 1.15);
        // Ramp position = bell * peak (continuous, no thresholds).
        const rampPos = clamp(shaped * peak, 0, 1);
        grad.addColorStop(u, _rgb(rampAt(rampPos)));
      }

      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.moveTo(a1x, a1y);
      ctx.lineTo(b1x, b1y);
      ctx.lineTo(b2x, b2y);
      ctx.lineTo(a2x, a2y);
      ctx.closePath();
      ctx.fill();
    }

    function drawRung(a, b) {
      // Rung connects a point on strand A to the same-s point on strand B.
      // Two problems we must avoid to not look "bolted on":
      //   1. A flat rectangular end intersecting a curved tube silhouette
      //      reads as a T-junction. → Shorten the rung so its visible
      //      portion terminates AT the outer edge of each tube, not
      //      past it, and not at the centerline.
      //   2. A hard-edged color change where rung meets strand reads as
      //      a separate piece glued on. → Over-paint each end with a
      //      dark longitudinal alpha gradient so the endpoints fade
      //      into the strand's own shadow edge.
      const dx = b.cx - a.cx, dy = b.cy - a.cy;
      const L = Math.hypot(dx, dy);
      if (L < 1) return;                 // collapsed at edge-on crossing
      const ux = dx / L, uy = dy / L;    // unit along rung
      const px = -uy, py = ux;           // unit perpendicular

      // Mobile perspective scale — a rung connects two same-s strand points,
      // so both endpoints share a single depth factor. Power curve matches
      // the strand + helix-radius scaling so all three taper together as
      // the ribbon recedes toward the vanishing point. Floor 0.48.
      const tDepth = (W < 700)
        ? 0.48 + 0.52 * Math.pow((a.s + b.s) * 0.5, 1.3)
        : 1.0;

      // Pull each endpoint INTO the strand by ~0.55 of strand thickness.
      // That's just past the outer tube edge, so the rung and strand
      // visibly overlap by a few pixels (= clean bonded joint).
      const inset = STRAND_THICKNESS * 0.48 * tDepth;
      if (inset * 2 >= L) return;        // strands too close to need a rung
      const ax2 = a.cx + ux * inset, ay2 = a.cy + uy * inset;
      const bx2 = b.cx - ux * inset, by2 = b.cy - uy * inset;

      const r = RUNG_THICKNESS * 0.5 * tDepth;
      const a1x = ax2 + px * r, a1y = ay2 + py * r;
      const a2x = ax2 - px * r, a2y = ay2 - py * r;
      const b1x = bx2 + px * r, b1y = by2 + py * r;
      const b2x = bx2 - px * r, b2y = by2 - py * r;

      // ---- 1. Transverse cross-section fill, using the strand ramp
      const gx1 = (a1x + b1x) * 0.5, gy1 = (a1y + b1y) * 0.5;
      const gx2 = (a2x + b2x) * 0.5, gy2 = (a2y + b2y) * 0.5;
      const grad = ctx.createLinearGradient(gx1, gy1, gx2, gy2);

      const frontness = ((a.z + b.z) * 0.5 + 1) * 0.5;
      // Rungs are thinner and darker than strands — never hit full specular.
      const peak = 0.28 + 0.48 * frontness;
      const STOPS = 9;
      for (let i = 0; i < STOPS; i++) {
        const u = i / (STOPS - 1);
        const d = Math.abs(u - 0.5) * 2;
        const bell = 0.5 * (1 + Math.cos(PI * clamp(d, 0, 1)));
        const rampPos = clamp(bell * peak, 0, 1);
        grad.addColorStop(u, _rgb(rampAt(rampPos)));
      }

      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.moveTo(a1x, a1y);
      ctx.lineTo(b1x, b1y);
      ctx.lineTo(b2x, b2y);
      ctx.lineTo(a2x, a2y);
      ctx.closePath();
      ctx.fill();

      // ---- 2. Longitudinal end-fade overlay — dark at both ends,
      // invisible through the middle.  This causes the rung's endpoints
      // to sink into the strand's own shadow tone rather than terminate
      // as a visible stub.  Alpha curve is tuned so only the last ~18%
      // of each end is actively darkened.
      const endFade = ctx.createLinearGradient(ax2, ay2, bx2, by2);
      endFade.addColorStop(0.00, 'rgba(26, 12, 7, 0.82)');
      endFade.addColorStop(0.10, 'rgba(26, 12, 7, 0.45)');
      endFade.addColorStop(0.20, 'rgba(26, 12, 7, 0.00)');
      endFade.addColorStop(0.80, 'rgba(26, 12, 7, 0.00)');
      endFade.addColorStop(0.90, 'rgba(26, 12, 7, 0.45)');
      endFade.addColorStop(1.00, 'rgba(26, 12, 7, 0.82)');
      ctx.fillStyle = endFade;
      ctx.beginPath();
      ctx.moveTo(a1x, a1y);
      ctx.lineTo(b1x, b1y);
      ctx.lineTo(b2x, b2y);
      ctx.lineTo(a2x, a2y);
      ctx.closePath();
      ctx.fill();
    }

    // --- tick ------------------------------------------------
    // Every frame:
    //   1. advance sheen positions
    //   2. re-sample both strands (geometry moves with time)
    //   3. build a flat list of { kind, a, b, z } segments
    //   4. z-sort back-to-front
    //   5. paint
    function tick(now) {
      rafId = 0;
      if (!visible) return;
      const time = now - t0;
      const dt = Math.min(48, now - lastTick);
      lastTick = now;

      sheenA += dt * SHEEN_SPEED_A;   if (sheenA >  1.3) sheenA -= 1.6;
      sheenB += dt * SHEEN_SPEED_B;   if (sheenB < -0.3) sheenB += 1.6;
      sheenC += dt * SHEEN_SPEED_A * 0.55; if (sheenC > 1.3) sheenC -= 1.6;

      resampleStrands(time);

      // Assemble segments with z for painter sort
      const segs = [];
      for (let i = 0; i < N_SAMPLES; i++) {
        const a = strandA[i], b = strandA[i + 1];
        segs.push({ k: 0, a, b, z: (a.z + b.z) * 0.5 });
      }
      for (let i = 0; i < N_SAMPLES; i++) {
        const a = strandB[i], b = strandB[i + 1];
        segs.push({ k: 0, a, b, z: (a.z + b.z) * 0.5 });
      }
      // Rungs at evenly-spaced s positions between the strands
      for (let r = 0; r < RUNG_COUNT; r++) {
        const sPos = (r + 0.5) / RUNG_COUNT;
        const idx = clamp(Math.round(sPos * N_SAMPLES), 0, N_SAMPLES);
        const aP = strandA[idx], bP = strandB[idx];
        segs.push({ k: 1, a: aP, b: bP, z: (aP.z + bP.z) * 0.5 });
      }

      // Painter's algorithm — back to front
      segs.sort((x, y) => x.z - y.z);

      ctx.clearRect(0, 0, W, H);
      // Soft back-light wash: a faint radial halo behind the helix belly
      // so the cream background doesn't feel dead around the metal.
      // Halo follows the helix: centered on the left column now that
      // the composition has flipped.
      const haloX = W < 700 ? W * 0.86 : W * 0.22;
      const halo = ctx.createRadialGradient(
        haloX, H * 0.5, Math.min(W, H) * 0.05,
        haloX, H * 0.5, Math.min(W, H) * 0.55
      );
      halo.addColorStop(0, 'rgba(196, 142, 110, 0.10)');
      halo.addColorStop(1, 'rgba(196, 142, 110, 0.00)');
      ctx.fillStyle = halo;
      ctx.fillRect(0, 0, W, H);

      for (let i = 0; i < segs.length; i++) {
        const seg = segs[i];
        if (seg.k === 0) drawStrandSegment(seg.a, seg.b);
        else             drawRung(seg.a, seg.b);
      }

      rafId = requestAnimationFrame(tick);
    }

    // --- reduced-motion static fallback ---------------------
    function paintStatic() {
      const time = performance.now() - t0;
      resampleStrands(time);
      const segs = [];
      for (let i = 0; i < N_SAMPLES; i++) {
        const a = strandA[i], b = strandA[i + 1];
        segs.push({ k: 0, a, b, z: (a.z + b.z) * 0.5 });
      }
      for (let i = 0; i < N_SAMPLES; i++) {
        const a = strandB[i], b = strandB[i + 1];
        segs.push({ k: 0, a, b, z: (a.z + b.z) * 0.5 });
      }
      for (let r = 0; r < RUNG_COUNT; r++) {
        const sPos = (r + 0.5) / RUNG_COUNT;
        const idx = clamp(Math.round(sPos * N_SAMPLES), 0, N_SAMPLES);
        const aP = strandA[idx], bP = strandB[idx];
        segs.push({ k: 1, a: aP, b: bP, z: (aP.z + bP.z) * 0.5 });
      }
      segs.sort((x, y) => x.z - y.z);
      ctx.clearRect(0, 0, W, H);
      for (let i = 0; i < segs.length; i++) {
        const seg = segs[i];
        if (seg.k === 0) drawStrandSegment(seg.a, seg.b);
        else             drawRung(seg.a, seg.b);
      }
    }

    // --- resize ---------------------------------------------
    function resize() {
      const rect = section.getBoundingClientRect();
      W = Math.max(1, Math.floor(rect.width));
      H = Math.max(1, Math.floor(rect.height));
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(W * dpr);
      canvas.height = Math.floor(H * dpr);
      canvas.style.width = W + 'px';
      canvas.style.height = H + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Responsive tuning — fewer samples on small screens for performance,
      // but strand + rung thickness stay close to desktop proportions so the
      // helix reads as the same metal sculpture instead of a thinned-out
      // wireframe. The helix sits BEHIND the copy on mobile (single-column
      // layout) so the heavier strokes don't have to dodge the text column.
      if (W < 700) {
        N_SAMPLES = 240;
        STRAND_THICKNESS = 28;
        RUNG_COUNT = 14;
        RUNG_THICKNESS = 4;
      } else if (W < 1100) {
        N_SAMPLES = 300;
        STRAND_THICKNESS = 26;
        RUNG_COUNT = 14;
        RUNG_THICKNESS = 4;
      } else {
        N_SAMPLES = 360;
        STRAND_THICKNESS = 30;
        RUNG_COUNT = 16;
        RUNG_THICKNESS = 4.5;
      }
      strandA = allocStrand(N_SAMPLES + 1);
      strandB = allocStrand(N_SAMPLES + 1);
    }

    // --- pointer tracking -----------------------------------
    // (Kept for future interactive effects; current renderer doesn't
    // couple to the cursor.)
    function onPointerMove(e) {
      const rect = section.getBoundingClientRect();
      pointer.x = e.clientX - rect.left;
      pointer.y = e.clientY - rect.top;
      pointer.active = true;
    }
    function onPointerLeave() {
      pointer.active = false;
      pointer.x = -9999;
      pointer.y = -9999;
    }
    section.addEventListener('pointermove', onPointerMove, { passive: true });
    section.addEventListener('pointerleave', onPointerLeave);

    // --- debounced resize -----------------------------------
    let resizeTimer = 0;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        resize();
        if (prefersReduced) paintStatic();
      }, 120);
    }, { passive: true });

    // --- on-screen pause ------------------------------------
    const io = ('IntersectionObserver' in window)
      ? new IntersectionObserver((entries) => {
          for (const entry of entries) {
            const wasVisible = visible;
            visible = entry.isIntersecting;
            if (visible && !wasVisible) {
              lastTick = performance.now();
              if (!prefersReduced && !rafId) rafId = requestAnimationFrame(tick);
            }
          }
        }, { threshold: 0 })
      : null;
    if (io) io.observe(section); else { visible = true; }

    // --- boot -----------------------------------------------
    resize();
    if (prefersReduced) {
      paintStatic();
    } else {
      lastTick = performance.now();
      if (visible) rafId = requestAnimationFrame(tick);
      else paintStatic();
    }
  })();


  /* ---- 9. Right-edge scroll progress nav -------------------
     Sets .is-active on the link whose target section intersects
     the centre band of the viewport.  A rootMargin of -45% top
     and -45% bottom leaves a ~10% tall sensing band straddling
     the vertical middle of the viewport, so the highlighted
     section matches what the viewer is actually reading. */
  (() => {
    const nav = document.querySelector('.scroll-nav');
    if (!nav) return;
    const links = Array.from(nav.querySelectorAll('.scroll-nav-link'));
    if (!links.length) return;
    const targets = links
      .map(link => {
        const id = link.dataset.target || link.getAttribute('href').replace(/^#/, '');
        const el = id ? document.getElementById(id) : null;
        return el ? { id, el, link } : null;
      })
      .filter(Boolean);
    if (!targets.length) return;

    const setActive = (id) => {
      for (const t of targets) {
        t.link.classList.toggle('is-active', t.id === id);
      }
    };

    /* Per-element theme sampling — each themable element in the
       nav (links + tick rules) looks at its own viewport Y, finds
       which page section is currently behind it, and inherits that
       section's data-theme.  So when the rail straddles a section
       boundary (e.g. halfway between hero/ink and science/cream)
       each segment gets its own contrast, and nothing can stick in
       a stale state — every scroll frame reconciles. */
    const themedSections = Array.from(document.querySelectorAll('section[data-theme]'));
    const themables = Array.from(nav.querySelectorAll('.scroll-nav-link, .sn-rule'));
    let themeRAF = null;
    const syncThemes = () => {
      themeRAF = null;
      /* Read all section rects once per frame */
      const srects = themedSections.map(s => {
        const r = s.getBoundingClientRect();
        return { top: r.top, bottom: r.bottom, theme: s.dataset.theme || 'dark' };
      });
      for (const el of themables) {
        const r = el.getBoundingClientRect();
        const mid = (r.top + r.bottom) / 2;
        let theme = 'dark';
        for (const sr of srects) {
          if (mid >= sr.top && mid < sr.bottom) { theme = sr.theme; break; }
        }
        if (el.dataset.theme !== theme) el.dataset.theme = theme;
      }
    };
    const scheduleThemeSync = () => {
      if (themeRAF == null) themeRAF = requestAnimationFrame(syncThemes);
    };
    window.addEventListener('scroll', scheduleThemeSync, { passive: true });
    window.addEventListener('resize', scheduleThemeSync, { passive: true });
    /* Initial paint — run synchronously so there is no dark-flash
       on load for a reader whose first paint is on a light section. */
    syncThemes();

    if (!('IntersectionObserver' in window)) {
      // Leave the first link active and bail — nothing else to do.
      setActive(targets[0].id);
      return;
    }

    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setActive(entry.target.id);
        }
      }
    }, {
      rootMargin: '-45% 0px -45% 0px',
      threshold: 0
    });
    targets.forEach(t => io.observe(t.el));
  })();


  /* ---- 10. Click-to-frame for in-page hash links -----------
     - Sections listed in TOP_ALIGN → always pin the section's
       top just below the sticky header (classic anchor jump).
     - Otherwise: centre if the section fits in the viewport;
       pin eyebrow under the header when it's taller than the
       viewport so the eyebrow is never scrolled past.
     Links to "#top" reset scroll.                                 */
  (() => {
    const prefersReduced = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = prefersReduced ? 'auto' : 'smooth';

    /* Sections that should always top-align under the header
       (classic anchor jump, no centring). */
    const TOP_ALIGN = new Set(['house', 'begin']);

    const getHeaderH = () => {
      const header = document.querySelector('.site-header');
      if (!header) return 0;
      const cs = getComputedStyle(header);
      if (cs.position === 'sticky' || cs.position === 'fixed') {
        return header.getBoundingClientRect().height;
      }
      return 0;
    };

    const hashLinks = document.querySelectorAll('a[href^="#"]');
    hashLinks.forEach(a => {
      a.addEventListener('click', (e) => {
        const href = a.getAttribute('href');
        if (!href || href === '#') return;

        if (href === '#top') {
          e.preventDefault();
          window.scrollTo({ top: 0, behavior });
          return;
        }

        const id = href.slice(1);
        const el = document.getElementById(id);
        if (!el) return;

        e.preventDefault();

        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight;
        const sectionH = rect.height;
        const pageY = rect.top + window.scrollY;
        const headerH = getHeaderH();

        let y;
        if (TOP_ALIGN.has(id)) {
          /* Always pin top under the header for these sections */
          y = pageY - headerH;
        } else if (sectionH <= vh) {
          /* Fits — centre with equal slack top and bottom */
          y = pageY - (vh - sectionH) / 2;
        } else {
          /* Taller than viewport — pin eyebrow under the header */
          y = pageY - headerH;
        }

        const maxY = document.documentElement.scrollHeight - vh;
        y = Math.max(0, Math.min(y, maxY));

        window.scrollTo({ top: y, behavior });
      });
    });
  })();


  /* ---- 11. Philosophy seam strand — horizontal DNA renderer
     A horizontal sibling of section 8's vertical helix, rendered on
     the canvas inside .phil-strand on philosophy.html.  Same metallic
     ramp, same traveling sheen, same z-sorted ribbon segments — only
     the axis has been rotated 90°: it now runs LEFT to RIGHT with a
     vertical meander, and the strands wrap above/below it instead of
     left/right of it.  Result: identical material reading, fitted to
     the seam between the hero photograph and the bone Core section. */
  (() => {
    const canvas = document.querySelector('.phil-strand-canvas');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const wrap = canvas.parentElement;

    // --- palette / ramp (identical to section 8) ----------
    const SHADOW_DEEP  = '#3A1B11';
    const SHADOW_WARM  = '#4C261B';
    const SHADOW_ROSE  = '#6B382A';
    const ROSE_SOFT    = '#945A40';
    const ROSE_GOLD    = '#C48E6E';
    const ROSE_LIGHT   = '#DBAD8B';
    const HIGHLIGHT    = '#F5CEA8';
    const HIGHLIGHT_HI = '#FBDFBF';
    const HOT_SPEC     = '#FFF4E2';
    function _hx(h) { const n = parseInt(h.slice(1),16); return [(n>>16)&255,(n>>8)&255,n&255]; }
    function _mix(a,b,t){ return [a[0]+(b[0]-a[0])*t, a[1]+(b[1]-a[1])*t, a[2]+(b[2]-a[2])*t]; }
    function _rgb(c){ return 'rgb('+(c[0]|0)+','+(c[1]|0)+','+(c[2]|0)+')'; }
    const RAMP = [
      [0.00,_hx(SHADOW_DEEP)],[0.10,_hx(SHADOW_WARM)],[0.22,_hx(SHADOW_ROSE)],
      [0.38,_hx(ROSE_SOFT)],[0.56,_hx(ROSE_GOLD)],[0.72,_hx(ROSE_LIGHT)],
      [0.86,_hx(HIGHLIGHT)],[0.94,_hx(HIGHLIGHT_HI)],[1.00,_hx(HOT_SPEC)]
    ];
    function rampAt(p){
      if (p<=0) return RAMP[0][1];
      if (p>=1) return RAMP[RAMP.length-1][1];
      for (let i=1;i<RAMP.length;i++){
        if (p<=RAMP[i][0]){
          const t=(p-RAMP[i-1][0])/(RAMP[i][0]-RAMP[i-1][0]);
          return _mix(RAMP[i-1][1],RAMP[i][1],t);
        }
      }
      return RAMP[RAMP.length-1][1];
    }
    function cosHalo(s,sCrest,width){
      const d=Math.abs(s-sCrest);
      if (d>=width) return 0;
      return 0.5*(1+Math.cos(PI*(d/width)));
    }

    const PI = Math.PI;
    const clamp = (v,lo,hi)=> v<lo?lo:(v>hi?hi:v);

    // --- tuning -------------------------------------------
    // The seam strand is much shorter than the science section's tower,
    // so we reduce twist cycles per length to keep the helix airy at
    // this aspect.
    const TWIST_CYCLES  = 4.2;        // ~4 turns across a wide strand reads as "rope" not "spring"
    const TWIST_SPEED   = 0.000060;   // rad/ms
    const BREATHE_SPEED = 0.00012;
    const BREATHE_AMT   = 0.06;
    // Vertical meander parameters — gentle two-wave sum so the line
    // crossing the seam has organic life without zig-zagging.
    const AXIS_W1     = 1.6;
    const AXIS_W2     = 3.2;
    const AXIS_DRIFT1 = 0.00002;
    const AXIS_DRIFT2 = 0.000015;
    let N_SAMPLES        = 320;
    let STRAND_THICKNESS = 6;
    let RUNG_COUNT       = 36;
    let RUNG_THICKNESS   = 2.4;
    let AMP_PX           = 26;        // helix radius (vertical, since axis is horizontal)
    let AMP_M1           = 18;        // primary vertical meander
    let AMP_M2           = 8;         // secondary vertical meander
    const SHEEN_SPEED_A  = 0.000060;
    const SHEEN_SPEED_B  = -0.000042;
    const SHEEN_WIDTH    = 0.22;
    const SHEEN_WIDTH2   = 0.34;

    // --- state -------------------------------------------
    let W=0, H=0, dpr=1;
    let visible = false, rafId = 0;
    let t0 = performance.now(), lastTick = t0;
    let strandA = null, strandB = null;
    let sheenA = 0, sheenB = 0.5, sheenC = 0.25;

    function allocStrand(n){
      const arr = new Array(n);
      for (let i=0;i<n;i++) arr[i] = { s:0, cx:0, cy:0, z:0, twist:0, px:0, py:0, d:1 };
      return arr;
    }

    // Horizontal axis: runs from x = -0.04*W to x = 1.04*W (overshoot
    // both sides so strands exit-frame without visible terminators).
    // Y meanders gently around the vertical center.
    function sampleAxis(s, time, out){
      const xLeft  = W * -0.04;
      const xRight = W *  1.04;
      const xSpan  = xRight - xLeft;
      const yMid   = H * 0.50;

      const ph1 = time * AXIS_DRIFT1;
      const ph2 = time * AXIS_DRIFT2 + 1.1;
      const axisX = xLeft + s * xSpan;
      const axisY = yMid
                  + AMP_M1 * Math.sin(s * AXIS_W1 * PI + ph1)
                  + AMP_M2 * Math.sin(s * AXIS_W2 * PI + ph2);

      // Tangent (d/ds): X is linear in s, Y is the derivative of the
      // meander. The tangent is mostly horizontal.
      const dxds = xSpan;
      const dyds = AMP_M1 * AXIS_W1 * PI * Math.cos(s * AXIS_W1 * PI + ph1)
                 + AMP_M2 * AXIS_W2 * PI * Math.cos(s * AXIS_W2 * PI + ph2);
      const tLen = Math.hypot(dxds, dyds) || 1;

      // Unit normal — rotate tangent 90° CCW. For a horizontal axis,
      // this is mostly vertical, so the helix wraps above/below.
      const nX = -dyds / tLen;
      const nY =  dxds / tLen;

      // PERSPECTIVE DEPTH ENVELOPE.  A single slow half-wave across
      // the length gives a smooth "coming forward, going back" read
      // (never any pinch-point thin spots).  A tiny 2.2-cycle ripple
      // stops it from being a mathematically perfect hump so the
      // motion still feels organic.
      //
      // depth ∈ [~0.85, ~1.33] — both endpoints stay visibly thick
      // while the centre swells forward.
      const depth = 0.80
                  + 0.50 * Math.sin(s * PI)
                  + 0.10 * Math.sin(s * 2.2 * PI + 0.6);
      const breathe = 1 + Math.sin(time*BREATHE_SPEED + s*0.6) * BREATHE_AMT;
      const amp = AMP_PX * depth * breathe;

      out.axisX = axisX; out.axisY = axisY;
      out.nX = nX; out.nY = nY; out.amp = amp;
      out.depth = depth;
    }

    const _axis = {};
    function sampleStrand(s, idx, time, dst){
      sampleAxis(s, time, _axis);
      const twist = s * TWIST_CYCLES * PI * 2 + time * TWIST_SPEED + idx * PI;
      const ct = Math.cos(twist);
      const st = Math.sin(twist);
      dst.s = s;
      dst.cx = _axis.axisX + _axis.amp * ct * _axis.nX;
      dst.cy = _axis.axisY + _axis.amp * ct * _axis.nY;
      dst.z = st;
      dst.twist = twist;
      dst.d = _axis.depth;  // perspective depth, used for per-segment thickness
    }

    function resampleStrands(time){
      const n = N_SAMPLES + 1;
      for (let i=0;i<n;i++){
        const s = i / N_SAMPLES;
        sampleStrand(s, 0, time, strandA[i]);
        sampleStrand(s, 1, time, strandB[i]);
      }
      computePerpendiculars(strandA);
      computePerpendiculars(strandB);
    }
    function computePerpendiculars(arr){
      const n = arr.length;
      for (let i=0;i<n;i++){
        let dx, dy;
        if (i===0){ dx = arr[1].cx-arr[0].cx; dy = arr[1].cy-arr[0].cy; }
        else if (i===n-1){ dx = arr[i].cx-arr[i-1].cx; dy = arr[i].cy-arr[i-1].cy; }
        else { dx = arr[i+1].cx-arr[i-1].cx; dy = arr[i+1].cy-arr[i-1].cy; }
        const L = Math.hypot(dx,dy) || 1;
        arr[i].px = -dy/L; arr[i].py = dx/L;
      }
    }

    const XS_STOPS = 13;
    function drawStrandSegment(a,b){
      // Ribbon thickness tracks perspective depth per-endpoint so the
      // strand fattens when it "comes forward" and slims when it
      // "recedes" — the ribbon becomes wider AND narrower in lockstep
      // with oval height, which is what sells the depth illusion.
      const rA = STRAND_THICKNESS * 0.5 * a.d;
      const rB = STRAND_THICKNESS * 0.5 * b.d;
      let a1x = a.cx+a.px*rA, a1y = a.cy+a.py*rA;
      let a2x = a.cx-a.px*rA, a2y = a.cy-a.py*rA;
      let b1x = b.cx+b.px*rB, b1y = b.cy+b.py*rB;
      let b2x = b.cx-b.px*rB, b2y = b.cy-b.py*rB;
      const tx = b.cx-a.cx, ty = b.cy-a.cy;
      const tL = Math.hypot(tx,ty);
      if (tL > 0.001){
        const ex = (tx/tL)*1.0, ey = (ty/tL)*1.0;
        a1x -= ex; a1y -= ey; a2x -= ex; a2y -= ey;
        b1x += ex; b1y += ey; b2x += ex; b2y += ey;
      }
      const gx1 = (a1x+b1x)*0.5, gy1 = (a1y+b1y)*0.5;
      const gx2 = (a2x+b2x)*0.5, gy2 = (a2y+b2y)*0.5;
      const grad = ctx.createLinearGradient(gx1,gy1,gx2,gy2);

      const avgZ = (a.z+b.z)*0.5;
      const frontness = (avgZ+1)*0.5;
      const sMid = (a.s+b.s)*0.5;
      const sheen =
        cosHalo(sMid, sheenA, SHEEN_WIDTH)*1.00 +
        cosHalo(sMid, sheenB, SHEEN_WIDTH)*0.85 +
        cosHalo(sMid, sheenC, SHEEN_WIDTH2)*0.60;
      const tw = (a.twist+b.twist)*0.5;
      const hotPos = clamp(0.5 + 0.20*Math.sin(tw*0.5+0.6), 0.22, 0.78);
      const litFactor = clamp(frontness*0.72 + Math.min(sheen,1.4)*0.45, 0, 1);
      const peak = 0.30 + 0.70*litFactor;

      const halfL = hotPos, halfR = 1-hotPos;
      for (let i=0;i<XS_STOPS;i++){
        const u = i/(XS_STOPS-1);
        const half = u<hotPos ? halfL : halfR;
        const d = half>1e-4 ? Math.abs(u-hotPos)/half : 1;
        const bell = 0.5*(1+Math.cos(PI*clamp(d,0,1)));
        const shaped = Math.pow(bell, 1.15);
        const rampPos = clamp(shaped*peak, 0, 1);
        grad.addColorStop(u, _rgb(rampAt(rampPos)));
      }

      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.moveTo(a1x,a1y); ctx.lineTo(b1x,b1y);
      ctx.lineTo(b2x,b2y); ctx.lineTo(a2x,a2y);
      ctx.closePath(); ctx.fill();
    }

    function drawRung(a,b){
      const dx = b.cx-a.cx, dy = b.cy-a.cy;
      const L = Math.hypot(dx,dy);
      if (L < 1) return;
      const ux = dx/L, uy = dy/L, px = -uy, py = ux;
      // Depth-scale the inset + thickness too, so rungs in close
      // sections look chunky and rungs in far sections look fine.
      const dAvg = (a.d + b.d) * 0.5;
      const inset = STRAND_THICKNESS*0.48*dAvg;
      if (inset*2 >= L) return;
      const ax2 = a.cx+ux*inset, ay2 = a.cy+uy*inset;
      const bx2 = b.cx-ux*inset, by2 = b.cy-uy*inset;
      const r = RUNG_THICKNESS*0.5*dAvg;
      const a1x = ax2+px*r, a1y = ay2+py*r;
      const a2x = ax2-px*r, a2y = ay2-py*r;
      const b1x = bx2+px*r, b1y = by2+py*r;
      const b2x = bx2-px*r, b2y = by2-py*r;

      const gx1 = (a1x+b1x)*0.5, gy1 = (a1y+b1y)*0.5;
      const gx2 = (a2x+b2x)*0.5, gy2 = (a2y+b2y)*0.5;
      const grad = ctx.createLinearGradient(gx1,gy1,gx2,gy2);
      const frontness = ((a.z+b.z)*0.5+1)*0.5;
      const peak = 0.28 + 0.48*frontness;
      const STOPS = 9;
      for (let i=0;i<STOPS;i++){
        const u = i/(STOPS-1);
        const d = Math.abs(u-0.5)*2;
        const bell = 0.5*(1+Math.cos(PI*clamp(d,0,1)));
        grad.addColorStop(u, _rgb(rampAt(clamp(bell*peak,0,1))));
      }
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.moveTo(a1x,a1y); ctx.lineTo(b1x,b1y);
      ctx.lineTo(b2x,b2y); ctx.lineTo(a2x,a2y);
      ctx.closePath(); ctx.fill();

      const endFade = ctx.createLinearGradient(ax2,ay2,bx2,by2);
      endFade.addColorStop(0.00,'rgba(26,12,7,0.82)');
      endFade.addColorStop(0.10,'rgba(26,12,7,0.45)');
      endFade.addColorStop(0.20,'rgba(26,12,7,0.00)');
      endFade.addColorStop(0.80,'rgba(26,12,7,0.00)');
      endFade.addColorStop(0.90,'rgba(26,12,7,0.45)');
      endFade.addColorStop(1.00,'rgba(26,12,7,0.82)');
      ctx.fillStyle = endFade;
      ctx.beginPath();
      ctx.moveTo(a1x,a1y); ctx.lineTo(b1x,b1y);
      ctx.lineTo(b2x,b2y); ctx.lineTo(a2x,a2y);
      ctx.closePath(); ctx.fill();
    }

    function paintFrame(time){
      resampleStrands(time);
      const segs = [];
      for (let i=0;i<N_SAMPLES;i++){
        const a = strandA[i], b = strandA[i+1];
        segs.push({k:0,a,b,z:(a.z+b.z)*0.5});
      }
      for (let i=0;i<N_SAMPLES;i++){
        const a = strandB[i], b = strandB[i+1];
        segs.push({k:0,a,b,z:(a.z+b.z)*0.5});
      }
      for (let r=0;r<RUNG_COUNT;r++){
        const sPos = (r+0.5)/RUNG_COUNT;
        const idx = clamp(Math.round(sPos*N_SAMPLES),0,N_SAMPLES);
        const aP = strandA[idx], bP = strandB[idx];
        segs.push({k:1,a:aP,b:bP,z:(aP.z+bP.z)*0.5});
      }
      segs.sort((x,y)=>x.z-y.z);
      ctx.clearRect(0,0,W,H);
      for (let i=0;i<segs.length;i++){
        const seg = segs[i];
        if (seg.k===0) drawStrandSegment(seg.a, seg.b);
        else            drawRung(seg.a, seg.b);
      }
    }

    function tick(now){
      rafId = 0;
      if (!visible) return;
      const time = now - t0;
      const dt = Math.min(48, now - lastTick);
      lastTick = now;
      sheenA += dt*SHEEN_SPEED_A;   if (sheenA >  1.3) sheenA -= 1.6;
      sheenB += dt*SHEEN_SPEED_B;   if (sheenB < -0.3) sheenB += 1.6;
      sheenC += dt*SHEEN_SPEED_A*0.55; if (sheenC > 1.3) sheenC -= 1.6;
      paintFrame(time);
      rafId = requestAnimationFrame(tick);
    }

    function resize(){
      const rect = wrap.getBoundingClientRect();
      W = Math.max(1, Math.floor(rect.width));
      H = Math.max(1, Math.floor(rect.height));
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(W*dpr);
      canvas.height = Math.floor(H*dpr);
      canvas.style.width = W+'px';
      canvas.style.height = H+'px';
      ctx.setTransform(dpr,0,0,dpr,0,0);
      // Responsive: thinner strands and tighter helix on small screens.
      if (W < 700){
        N_SAMPLES = 220; STRAND_THICKNESS = 4.5; RUNG_COUNT = 22;
        RUNG_THICKNESS = 1.6; AMP_PX = 18; AMP_M1 = 12; AMP_M2 = 5;
      } else if (W < 1100){
        N_SAMPLES = 280; STRAND_THICKNESS = 5.5; RUNG_COUNT = 30;
        RUNG_THICKNESS = 2.0; AMP_PX = 22; AMP_M1 = 16; AMP_M2 = 7;
      } else {
        N_SAMPLES = 360; STRAND_THICKNESS = 6.5; RUNG_COUNT = 40;
        RUNG_THICKNESS = 2.6; AMP_PX = 28; AMP_M1 = 18; AMP_M2 = 8;
      }
      strandA = allocStrand(N_SAMPLES + 1);
      strandB = allocStrand(N_SAMPLES + 1);
    }

    let resizeTimer = 0;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        resize();
        if (prefersReduced) paintFrame(performance.now()-t0);
      }, 120);
    }, { passive: true });

    const io = ('IntersectionObserver' in window)
      ? new IntersectionObserver((entries) => {
          for (const entry of entries){
            const wasVisible = visible;
            visible = entry.isIntersecting;
            if (visible && !wasVisible){
              lastTick = performance.now();
              if (!prefersReduced && !rafId) rafId = requestAnimationFrame(tick);
            }
          }
        }, { threshold: 0 })
      : null;
    if (io) io.observe(wrap); else { visible = true; }

    resize();
    if (prefersReduced){
      paintFrame(performance.now()-t0);
    } else {
      lastTick = performance.now();
      if (visible) rafId = requestAnimationFrame(tick);
      else paintFrame(performance.now()-t0);
    }
  })();


  /* ---- 12. Opening — split scroll reveal ------------------
     Present on philosophy.html. .phil-opening-stage is tall; inside,
     .phil-opening-grid is position: sticky top: 0 for 100vh.

     As the user scrolls, progress p ∈ [0, 1] drives two things:
       1) A crossfade between the SEALED frame (k=0) and the PLACED
          frame (k=1) on the right column. Because the two photos
          share camera + crop, this reads as the lid dissolving off.
       2) A rose-gold reveal of the "Synergy." word on the left. The
          word is blurred + offset until progress crosses a threshold,
          then settles in time with the image.

     Prefers-reduced-motion: this JS is skipped; CSS shows the final
     state (placed image + revealed word). */
  (function openingReveal(){
    const section = document.querySelector('.phil-opening');
    if (!section) return;

    const stage   = section.querySelector('.phil-opening-stage');
    const frames  = Array.from(section.querySelectorAll('.phil-opening-frame'));
    const word    = section.querySelector('.phil-opening-word');

    if (!stage || frames.length < 2) return;

    // --- Reduced-motion path: show final state statically, done.
    if (prefersReduced){
      frames.forEach((f, i) => {
        f.style.opacity = (i === frames.length - 1) ? '1' : '0';
      });
      if (word) word.classList.add('is-revealed');
      return;
    }

    // --- Mobile fallback: when the sticky stage is folded (CSS sets
    // height:auto in the 760px query), scroll-driven progress math
    // collapses. Drive the reveal off a simple IntersectionObserver
    // threshold and fade directly to the placed frame.
    const mq = window.matchMedia('(max-width: 760px)');
    function mountMobile(){
      frames[0].style.opacity = '0';
      frames[frames.length - 1].style.opacity = '1';
      if (!word) return;
      const io = ('IntersectionObserver' in window)
        ? new IntersectionObserver((entries) => {
            entries.forEach(e => {
              if (e.isIntersecting) word.classList.add('is-revealed');
            });
          }, { threshold: 0.45 })
        : null;
      if (io) io.observe(word); else word.classList.add('is-revealed');
    }
    if (mq.matches){ mountMobile(); return; }

    let ticking = false;

    function clamp(v,a,b){ return v < a ? a : (v > b ? b : v); }
    function easeInOutCubic(x){
      return x < 0.5 ? 4*x*x*x : 1 - Math.pow(-2*x + 2, 3) / 2;
    }

    // Crossfade progress: the image doesn't wait for the last pixel
    // of the stage. It runs from p=0.10 (a beat of hold on the sealed
    // box while the first half of the sentence settles) to p=0.70
    // (finishes before the section exits so it can breathe).
    const X_START = 0.10;
    const X_END   = 0.70;
    // Word reveal threshold — Synergy. fades in as the image is at
    // roughly 60% through the crossfade.
    const WORD_IN  = 0.45;
    const WORD_OUT = 0.62;  // hysteresis — stays revealed above this

    let wordRevealed = false;

    function paint(p){
      // Crossfade curve
      let fx = 0;
      if (p <= X_START)       fx = 0;
      else if (p >= X_END)    fx = 1;
      else                    fx = easeInOutCubic((p - X_START) / (X_END - X_START));

      // fx ∈ [0,1] — 0 = fully sealed, 1 = fully placed
      frames[0].style.opacity = (1 - fx).toFixed(4);
      frames[1].style.opacity = fx.toFixed(4);
      frames[0].classList.toggle('is-active', fx < 0.5);
      frames[1].classList.toggle('is-active', fx >= 0.5);

      // Word reveal with hysteresis so it doesn't flicker at the edge
      if (word){
        if (!wordRevealed && p >= WORD_IN)  { word.classList.add('is-revealed');    wordRevealed = true;  }
        else if (wordRevealed && p < (WORD_IN - 0.06)) { word.classList.remove('is-revealed'); wordRevealed = false; }
      }
    }

    function update(){
      ticking = false;
      const rect  = stage.getBoundingClientRect();
      const vh    = window.innerHeight;
      const total = rect.height - vh;
      if (total <= 0){ paint(0); return; }
      const p = clamp((-rect.top) / total, 0, 1);
      paint(p);
    }

    function onScroll(){
      if (!ticking){
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }

    // If the viewport crosses the 760px breakpoint mid-session,
    // remount into the right mode. Keep it simple — full reload
    // path isn't necessary; just toggle classes.
    mq.addEventListener?.('change', () => {
      if (mq.matches){
        // Tear down desktop listeners by setting a flag; simpler to
        // just reload the page on breakpoint change for this demo.
        window.location.reload();
      }
    });

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  })();


  /* ---- N. Ingredient tabs — Earth / Lab / Skin ------------- */
  (function initIngredientTabs() {
    const roots = document.querySelectorAll('[data-ingredients]');
    if (!roots.length) return;

    roots.forEach(root => {
      const tabs   = Array.from(root.querySelectorAll('[role="tab"]'));
      const panels = Array.from(root.querySelectorAll('[role="tabpanel"]'));
      if (!tabs.length || !panels.length) return;

      // Once JS boots we drop the `hidden` attribute on every panel so
      // the stacking trick in CSS (.phil-ingredients-stage) can reserve
      // space for the tallest panel. Visibility is now governed by the
      // `is-active` class alone — this prevents the adjacent image
      // from reflowing when tabs switch. The `hidden` attribute
      // remains as the no-JS fallback up to this point.
      panels.forEach(p => p.removeAttribute('hidden'));

      function activate(idx, focusTab) {
        tabs.forEach((t, i) => {
          const on = i === idx;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
        });
        panels.forEach((p, i) => {
          p.classList.toggle('is-active', i === idx);
        });
        if (focusTab && tabs[idx]) tabs[idx].focus();
      }

      // Normalize initial state from markup (first tab defaults to active
      // if none are marked).
      let initial = tabs.findIndex(t => t.getAttribute('aria-selected') === 'true');
      if (initial < 0) initial = 0;
      activate(initial, false);

      tabs.forEach((tab, i) => {
        tab.addEventListener('click', (e) => {
          e.preventDefault();
          activate(i, false);
        });
        tab.addEventListener('keydown', (e) => {
          const last = tabs.length - 1;
          switch (e.key) {
            case 'ArrowRight':
            case 'ArrowDown':
              e.preventDefault();
              activate(i === last ? 0 : i + 1, true);
              break;
            case 'ArrowLeft':
            case 'ArrowUp':
              e.preventDefault();
              activate(i === 0 ? last : i - 1, true);
              break;
            case 'Home':
              e.preventDefault();
              activate(0, true);
              break;
            case 'End':
              e.preventDefault();
              activate(last, true);
              break;
            case 'Enter':
            case ' ':
              e.preventDefault();
              activate(i, false);
              break;
          }
        });
      });
    });
  })();


})();
